/** En-tête de colonne triable — chevron en bout de libellé. */
import { useState } from "react";

export type SortDir = "asc" | "desc";
export type SortKind = "text" | "date" | "num";

export function useTableSort<K extends string>() {
  const [key, setKey] = useState<K | null>(null);
  const [dir, setDir] = useState<SortDir>("asc");

  function onSort(next: K, kind: SortKind) {
    if (key === next) {
      setDir((d) => (d === "asc" ? "desc" : "asc"));
      return;
    }
    setKey(next);
    setDir(kind === "text" ? "asc" : "desc");
  }

  return { key, dir, onSort };
}

function asSortValue(value: unknown, kind: SortKind): string | number | null {
  if (value === null || value === undefined || value === "") return null;
  if (kind === "num") {
    const n = typeof value === "number" ? value : Number(String(value).replace(",", "."));
    return Number.isFinite(n) ? n : null;
  }
  const s = String(value).trim();
  return s === "" || s === "—" ? null : s;
}

export function sortRows<T>(rows: T[], dir: SortDir, kind: SortKind, pick: (row: T) => unknown): T[] {
  const copy = [...rows];
  copy.sort((a, b) => {
    const av = asSortValue(pick(a), kind);
    const bv = asSortValue(pick(b), kind);
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    const cmp =
      typeof av === "number" && typeof bv === "number"
        ? av - bv
        : String(av).localeCompare(String(bv), "fr", { numeric: true, sensitivity: "base" });
    return dir === "asc" ? cmp : -cmp;
  });
  return copy;
}

function Chevron({ dir }: { dir: SortDir }) {
  return (
    <svg className={dir === "desc" ? "is-desc" : "is-asc"} width="11" height="11" viewBox="0 0 11 11" aria-hidden="true">
      <path d="M2.2 7.1 5.5 3.8 8.8 7.1" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

type SortThProps = {
  label: string;
  active: boolean;
  dir: SortDir;
  onSort: () => void;
  className?: string;
};

export function SortTh({ label, active, dir, onSort, className }: SortThProps) {
  const end = className?.split(" ").includes("is-num");
  return (
    <th scope="col" className={className} aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}>
      <button type="button" className={`fx-sort${active ? " is-on" : ""}${end ? " is-end" : ""}`} onClick={onSort}>
        <span>{label}</span>
        <Chevron dir={active ? dir : "asc"} />
      </button>
    </th>
  );
}
